import React, { useState, useEffect } from 'react';
import Login from './components/Login';
import Quiz from './components/Quiz';
import CategorySelector from './components/CategorySelector';
import StartPage from './components/StartPage';
import Statistics from './components/Statistics';
import UserManagement from './components/UserManagement';
import handballQuestions from './data/questions_live.json';
import handballAnswers from './data/answers_live.json';
import soccerQuestions from './data/questions_dummy.json';
import soccerAnswers from './data/answers_dummy.json';
import language from './language';
import db from './db';

const App = () => {
    const [selectedSport, setSelectedSport] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [showNextButton, setShowNextButton] = useState(false);
    const [answeredQuestions, setAnsweredQuestions] = useState(0);
    const [correctCount, setCorrectCount] = useState(0);
    const [wrongCount, setWrongCount] = useState(0);
    const [results, setResults] = useState([]);
    const [showStatistics, setShowStatistics] = useState(false);
    const [users, setUsers] = useState(db.data.users);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        if (selectedSport) {
            let questionsData = selectedSport === 'handball' ? handballQuestions : soccerQuestions;
            let answersData = selectedSport === 'handball' ? handballAnswers : soccerAnswers;

            let filteredQuestions = [];
            if (selectedCategory === 'all') {
                Object.values(questionsData).forEach(category => {
                    filteredQuestions = filteredQuestions.concat(category);
                });
            } else {
                filteredQuestions = questionsData[selectedCategory] || [];
            }

            const randomQuestions = filteredQuestions.sort(() => 0.5 - Math.random()).slice(0, 30);
            setQuestions(randomQuestions);
            setAnswers(answersData);
            setCurrentQuestionIndex(0);
            setShowNextButton(false);
            setAnsweredQuestions(0);
            setCorrectCount(0);
            setWrongCount(0);
            setResults([]);
            setShowStatistics(false);
        }
    }, [selectedSport, selectedCategory]);

    const handleAnswerSubmit = (isCorrect) => {
        setShowNextButton(true);
        setAnsweredQuestions(answeredQuestions + 1);
        if (isCorrect) {
            setCorrectCount(correctCount + 1);
        } else {
            setWrongCount(wrongCount + 1);
        }
        setResults([...results, { question: questions[currentQuestionIndex], isCorrect }]);
    };

    const handleNextQuestion = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
            setShowNextButton(false);
        } else {
            setShowStatistics(true);
        }
    };

    const handleRestart = () => {
        setSelectedSport(null);
    };

    const addUser = async (name) => {
        const newUser = { name, sports: [] };
        db.data.users.push(newUser);
        await db.write();
        setUsers([...users, newUser]);
    };

    const updateUserSports = async (name, sports) => {
        const user = db.data.users.find(user => user.name === name);
        if (user) {
            user.sports = sports;
            await db.write();
            setUsers(users.map(user => user.name === name ? { ...user, sports } : user));
        }
    };

    const handleLogin = (username, password) => {
        const user = db.data.users.find(
            (user) => user.name === username && user.password === password
        );
        if (user) {
            setCurrentUser(user);
            return user;
        }
        return null;
    };

    const handleLogout = () => {
        setCurrentUser(null);
        setSelectedSport(null);
    };

    return (
        <div className="app">
            {!currentUser ? (
                <Login onLogin={handleLogin} />
            ) : currentUser.role === 'admin' ? (
                <UserManagement users={users} addUser={addUser} updateUserSports={updateUserSports} />
            ) : !selectedSport ? (
                <StartPage setSelectedSport={setSelectedSport} />
            ) : showStatistics ? (
                <Statistics results={results} onRestart={handleRestart} />
            ) : (
                <>
                    <h1>{language.quizApplication}</h1>
                    <CategorySelector setSelectedCategory={setSelectedCategory} categories={Object.keys(selectedSport === 'handball' ? handballQuestions : soccerQuestions)} />
                    {questions.length > 0 && (
                        <Quiz
                            question={questions[currentQuestionIndex].question}
                            correctAnswers={answers[questions[currentQuestionIndex]?.number]}
                            onAnswerSubmit={handleAnswerSubmit}
                            showNextButton={showNextButton}
                            onNextQuestion={handleNextQuestion}
                            isLastQuestion={currentQuestionIndex === questions.length - 1}
                            totalQuestions={questions.length}
                            answeredQuestions={answeredQuestions}
                            correctCount={correctCount}
                            wrongCount={wrongCount}
                        />
                    )}
                </>
            )}
            {currentUser && (
                <button onClick={handleLogout}>{language.logout}</button>
            )}
        </div>
    );
};

export default App;